import type { Clue } from "./types.ts";
import { Grid } from "./solver-grid.ts";

export interface Relation {
  catA: number;
  valA: number;
  catB: number;
  valB: number;
  orderCat: number;
  ok: (p: number, q: number) => boolean;
  distinct: boolean;
}

export interface Conditional {
  pCat: number;
  pVal: number;
  qCat: number;
  qVal: number;
  rCat: number;
  rVal: number;
  sCat: number;
  sVal: number;
}

export type LinkState = "held" | "broken" | "open";

export function linkState(grid: Grid, i: number, a: number, j: number, b: number): LinkState {
  if (!grid.get(i, j, a, b)) return "broken";
  const forward = grid.candidates(i, j, a);
  const back = grid.candidates(j, i, b);
  return forward.length === 1 && back.length === 1 ? "held" : "open";
}

export function pinLink(grid: Grid, i: number, a: number, j: number, b: number): boolean {
  let changed = false;
  for (let x = 0; x < grid.M; x++) {
    if (x !== b && grid.get(i, j, a, x)) {
      grid.eliminate(i, j, a, x);
      changed = true;
    }
    if (x !== a && grid.get(i, j, x, b)) {
      grid.eliminate(i, j, x, b);
      changed = true;
    }
  }
  return changed;
}

export function applyConditionals(
  grid: Grid,
  conditionals: Conditional[],
): { changed: boolean; contradiction: boolean } {
  let changed = false;
  for (const c of conditionals) {
    const antecedent = linkState(grid, c.pCat, c.pVal, c.qCat, c.qVal);
    const consequent = linkState(grid, c.rCat, c.rVal, c.sCat, c.sVal);

    if (antecedent === "held" && consequent === "broken") return { changed, contradiction: true };

    if (antecedent === "held" && consequent === "open") {
      if (pinLink(grid, c.rCat, c.rVal, c.sCat, c.sVal)) changed = true;
    } else if (consequent === "broken" && antecedent === "open") {
      grid.eliminate(c.pCat, c.qCat, c.pVal, c.qVal);
      changed = true;
      if (grid.candidates(c.pCat, c.qCat, c.pVal).length === 0)
        return { changed, contradiction: true };
    }
  }
  return { changed, contradiction: false };
}

export interface Counting {
  links: { iCat: number; iVal: number; jCat: number; jVal: number }[];
  trueCount: number;
  worlds: number[];
}

export function subsetMasks(n: number, k: number): number[] {
  const out: number[] = [];
  for (let mask = 0; mask < 1 << n; mask++) {
    let bits = 0;
    for (let b = 0; b < n; b++) if (mask & (1 << b)) bits++;
    if (bits === k) out.push(mask);
  }
  return out;
}

export function applyCounting(
  grid: Grid,
  countings: Counting[],
): { changed: boolean; contradiction: boolean } {
  let changed = false;
  for (const c of countings) {
    const states = c.links.map((l) => linkState(grid, l.iCat, l.iVal, l.jCat, l.jVal));
    const live = c.worlds.filter((mask) =>
      c.links.every((_, b) => {
        const wantsTrue = (mask & (1 << b)) !== 0;
        return wantsTrue ? states[b] !== "broken" : states[b] !== "held";
      }),
    );
    if (live.length === 0) return { changed, contradiction: true };

    for (let b = 0; b < c.links.length; b++) {
      const bit = 1 << b;
      const alwaysTrue = live.every((mask) => (mask & bit) !== 0);
      const alwaysFalse = live.every((mask) => (mask & bit) === 0);
      const l = c.links[b]!;
      if (alwaysTrue && states[b] === "open") {
        if (pinLink(grid, l.iCat, l.iVal, l.jCat, l.jVal)) changed = true;
      } else if (alwaysFalse && states[b] !== "broken") {
        grid.eliminate(l.iCat, l.jCat, l.iVal, l.jVal);
        changed = true;
        if (grid.candidates(l.iCat, l.jCat, l.iVal).length === 0)
          return { changed, contradiction: true };
      }
    }
  }
  return { changed, contradiction: false };
}

export interface Between {
  aCat: number;
  aVal: number;
  bCat: number;
  bVal: number;
  cCat: number;
  cVal: number;
  orderCat: number;
}

export function isBetween(pa: number, pb: number, pc: number): boolean {
  return (pb < pa && pa < pc) || (pc < pa && pa < pb);
}

export interface Constraints {
  relations: Relation[];
  betweens: Between[];
  conditionals: Conditional[];
  countings: Counting[];
}

export const NO_CONSTRAINTS: Constraints = {
  relations: [],
  betweens: [],
  conditionals: [],
  countings: [],
};

export interface Compiled extends Constraints {
  contradiction: boolean;
}

export function compile(grid: Grid, clues: Clue[]): Compiled {
  const compiled: Compiled = {
    relations: [],
    betweens: [],
    conditionals: [],
    countings: [],
    contradiction: false,
  };
  for (const clue of clues) compileClue(grid, clue, compiled);
  return compiled;
}

function compileClue(grid: Grid, clue: Clue, out: Compiled): void {
  switch (clue.kind) {
    case "same":
      if (!pinPair(grid, clue.a.cat, clue.a.val, clue.b.cat, clue.b.val)) out.contradiction = true;
      return;
    case "not":
      grid.eliminate(clue.a.cat, clue.b.cat, clue.a.val, clue.b.val);
      return;
    case "either":
      if (!narrowToEither(grid, clue)) out.contradiction = true;
      return;
    case "conditional":
      out.conditionals.push(toConditional(clue));
      return;
    case "cardinality":
      out.countings.push(toCounting(clue));
      return;
    case "between":
      out.betweens.push(toBetween(clue));
      return;
    case "immediate":
    case "before":
    case "distance":
    case "adjacent":
      out.relations.push(toRelation(clue));
      return;
    case "liar":
      return;
  }
}

function pinPair(grid: Grid, i: number, a: number, j: number, b: number): boolean {
  for (let x = 0; x < grid.M; x++) {
    if (x !== b) grid.eliminate(i, j, a, x);
    if (x !== a) grid.eliminate(i, j, x, b);
  }
  return grid.get(i, j, a, b);
}

function narrowToEither(grid: Grid, clue: Clue): boolean {
  const j = clue.b.cat;
  for (let x = 0; x < grid.M; x++)
    if (x !== clue.b.val && x !== clue.c!.val) grid.eliminate(clue.a.cat, j, clue.a.val, x);
  return grid.candidates(clue.a.cat, j, clue.a.val).length > 0;
}

function toConditional(clue: Clue): Conditional {
  return {
    pCat: clue.a.cat,
    pVal: clue.a.val,
    qCat: clue.b.cat,
    qVal: clue.b.val,
    rCat: clue.c!.cat,
    rVal: clue.c!.val,
    sCat: clue.d!.cat,
    sVal: clue.d!.val,
  };
}

function toCounting(clue: Clue): Counting {
  const links = clue.group!.map((m) => ({
    iCat: m.a.cat,
    iVal: m.a.val,
    jCat: m.b.cat,
    jVal: m.b.val,
  }));
  return {
    links,
    trueCount: clue.trueCount!,
    worlds: subsetMasks(links.length, clue.trueCount!),
  };
}

function toBetween(clue: Clue): Between {
  return {
    aCat: clue.a.cat,
    aVal: clue.a.val,
    bCat: clue.b.cat,
    bVal: clue.b.val,
    cCat: clue.c!.cat,
    cVal: clue.c!.val,
    orderCat: clue.orderCat!,
  };
}

function toRelation(clue: Clue): Relation {
  return {
    catA: clue.a.cat,
    valA: clue.a.val,
    catB: clue.b.cat,
    valB: clue.b.val,
    orderCat: clue.orderCat!,
    ok: relationPredicate(clue.kind, clue.distance ?? 1),
    distinct: true,
  };
}

export function relationPredicate(
  kind: Clue["kind"],
  d: number,
): (p: number, q: number) => boolean {
  switch (kind) {
    case "immediate":
      return (p, q) => q - p === d;
    case "before":
      return (p, q) => q > p;
    case "distance":
      return (p, q) => Math.abs(p - q) === d;
    case "adjacent":
      return (p, q) => Math.abs(p - q) === 1;
    default:
      return () => true;
  }
}

export function applyRelations(
  grid: Grid,
  relations: Relation[],
): {
  changed: boolean;
  contradiction: boolean;
} {
  let changed = false;
  for (const r of relations) {
    const posA = grid.candidates(r.catA, r.orderCat, r.valA);
    const posB = grid.candidates(r.catB, r.orderCat, r.valB);

    for (const p of posA)
      if (!posB.some((q) => r.ok(p, q)))
        changed = grid.eliminate(r.catA, r.orderCat, r.valA, p) || changed;
    for (const q of posB)
      if (!posA.some((p) => r.ok(p, q)))
        changed = grid.eliminate(r.catB, r.orderCat, r.valB, q) || changed;

    if (r.distinct && r.catA !== r.catB)
      changed = grid.eliminate(r.catA, r.catB, r.valA, r.valB) || changed;
  }

  for (const r of relations) {
    if (grid.candidates(r.catA, r.orderCat, r.valA).length === 0)
      return { changed, contradiction: true };
    if (grid.candidates(r.catB, r.orderCat, r.valB).length === 0)
      return { changed, contradiction: true };
  }
  return { changed, contradiction: false };
}

export function applyBetweens(
  grid: Grid,
  betweens: Between[],
): {
  changed: boolean;
  contradiction: boolean;
} {
  let changed = false;
  for (const t of betweens) {
    const posA = grid.candidates(t.aCat, t.orderCat, t.aVal);
    const posB = grid.candidates(t.bCat, t.orderCat, t.bVal);
    const posC = grid.candidates(t.cCat, t.orderCat, t.cVal);

    for (const pa of posA)
      if (!posB.some((pb) => posC.some((pc) => isBetween(pa, pb, pc))))
        changed = grid.eliminate(t.aCat, t.orderCat, t.aVal, pa) || changed;
    for (const pb of posB)
      if (!posA.some((pa) => posC.some((pc) => isBetween(pa, pb, pc))))
        changed = grid.eliminate(t.bCat, t.orderCat, t.bVal, pb) || changed;
    for (const pc of posC)
      if (!posA.some((pa) => posB.some((pb) => isBetween(pa, pb, pc))))
        changed = grid.eliminate(t.cCat, t.orderCat, t.cVal, pc) || changed;

    if (t.aCat !== t.bCat) changed = grid.eliminate(t.aCat, t.bCat, t.aVal, t.bVal) || changed;
    if (t.aCat !== t.cCat) changed = grid.eliminate(t.aCat, t.cCat, t.aVal, t.cVal) || changed;
    if (t.bCat !== t.cCat) changed = grid.eliminate(t.bCat, t.cCat, t.bVal, t.cVal) || changed;
  }

  for (const t of betweens) {
    if (grid.candidates(t.aCat, t.orderCat, t.aVal).length === 0)
      return { changed, contradiction: true };
    if (grid.candidates(t.bCat, t.orderCat, t.bVal).length === 0)
      return { changed, contradiction: true };
    if (grid.candidates(t.cCat, t.orderCat, t.cVal).length === 0)
      return { changed, contradiction: true };
  }
  return { changed, contradiction: false };
}

export function pairUniqueness(grid: Grid): { changed: boolean; contradiction: boolean } {
  let changed = false;
  for (let i = 0; i < grid.K; i++)
    for (let j = 0; j < grid.K; j++) {
      if (i === j) continue;
      for (let a = 0; a < grid.M; a++) {
        const cand = grid.candidates(i, j, a);
        if (cand.length === 0) return { changed, contradiction: true };
        if (cand.length === 1) {
          const b = cand[0]!;
          for (let x = 0; x < grid.M; x++)
            if (x !== a) changed = grid.eliminate(i, j, x, b) || changed;
        }
      }
    }
  return { changed, contradiction: false };
}

export function transitivity(grid: Grid): { changed: boolean; contradiction: boolean } {
  let changed = false;
  const { K, M } = grid;
  for (let i = 0; i < K; i++)
    for (let k = 0; k < K; k++) {
      if (i === k) continue;
      for (let j = 0; j < K; j++) {
        if (j === i || j === k) continue;
        for (let a = 0; a < M; a++)
          for (let c = 0; c < M; c++) {
            if (!grid.get(i, k, a, c)) continue;
            let bridge = false;
            for (let b = 0; b < M; b++)
              if (grid.get(i, j, a, b) && grid.get(j, k, b, c)) {
                bridge = true;
                break;
              }
            if (!bridge) {
              grid.eliminate(i, k, a, c);
              changed = true;
            }
          }
      }
    }
  for (let i = 0; i < K; i++)
    for (let j = 0; j < K; j++) {
      if (i === j) continue;
      for (let a = 0; a < M; a++)
        if (grid.candidates(i, j, a).length === 0) return { changed, contradiction: true };
    }
  return { changed, contradiction: false };
}

export interface Techniques {
  relations: boolean;
  ruleB: boolean;
}

export const ALL_TECHNIQUES: Techniques = { relations: true, ruleB: true };

export interface PropagateResult {
  contradiction: boolean;
  usedRelation: boolean;
  usedRuleB: boolean;
}

interface Pass {
  enabled: boolean;
  run: () => { changed: boolean; contradiction: boolean };
  marks?: "usedRelation" | "usedRuleB";
}

export function propagate(
  grid: Grid,
  { relations, betweens, conditionals, countings }: Constraints,
  use: Techniques = ALL_TECHNIQUES,
): PropagateResult {
  const passes: Pass[] = [
    { enabled: conditionals.length > 0, run: () => applyConditionals(grid, conditionals) },
    { enabled: countings.length > 0, run: () => applyCounting(grid, countings) },
    {
      enabled: use.relations && relations.length > 0,
      run: () => applyRelations(grid, relations),
      marks: "usedRelation",
    },
    {
      enabled: use.relations && betweens.length > 0,
      run: () => applyBetweens(grid, betweens),
      marks: "usedRelation",
    },
    { enabled: true, run: () => pairUniqueness(grid) },
    { enabled: use.ruleB, run: () => transitivity(grid), marks: "usedRuleB" },
  ];

  const result: PropagateResult = { contradiction: false, usedRelation: false, usedRuleB: false };

  for (;;) {
    let changed = false;
    for (const pass of passes) {
      if (!pass.enabled) continue;
      const outcome = pass.run();
      if (outcome.contradiction) return { ...result, contradiction: true };
      if (!outcome.changed) continue;
      changed = true;
      if (pass.marks) result[pass.marks] = true;
    }
    if (!changed) return result;
  }
}
